
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../services/db';
import { User, Client, TimeEntry, PayPeriod } from '../types';
import { Plus, Search, Edit2, Trash2, X } from 'lucide-react';

interface ClientsProps {
  user: User;
  onViewClient: (id: string) => void;
}

type Timeframe = 'all' | 'month' | '30days' | 'year';

const Clients: React.FC<ClientsProps> = ({ user, onViewClient }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [search, setSearch] = useState('');
  const [timeframe, setTimeframe] = useState<Timeframe>('all');
  
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', rate: 0 });

  useEffect(() => {
    setClients(db.getClients(user.id));
    setEntries(db.getEntries(user.id));
    setPeriods(db.getPayPeriods(user.id));
  }, [user.id]);

  const handleOpenAdd = () => {
    setEditingClient(null);
    setFormData({ name: '', email: '', rate: user.hourlyRate || 50 });
    setShowModal(true);
  };

  const handleOpenEdit = (e: React.MouseEvent, client: Client) => {
    e.stopPropagation();
    setEditingClient(client);
    setFormData({ name: client.name, email: client.email || '', rate: client.defaultRate });
    setShowModal(true);
  };

  const handleDelete = (e: React.MouseEvent, clientId: string, name: string) => {
    e.stopPropagation();
    if (window.confirm(`Permanently delete "${name}" from your client list? Historical data will remain preserved in the database for auditing.`)) {
      db.deleteClient(clientId);
      setClients(clients.filter(c => c.id !== clientId));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const client: Client = {
      id: editingClient ? editingClient.id : `client-${Date.now()}`,
      userId: user.id,
      name: formData.name,
      email: formData.email || undefined,
      defaultRate: formData.rate,
      createdAt: editingClient ? editingClient.createdAt : new Date().toISOString()
    };
    db.saveClient(client);
    
    if (editingClient) {
      setClients(clients.map(c => c.id === client.id ? client : c));
    } else {
      setClients([...clients, client]);
    }
    
    setShowModal(false);
  };

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  const isInTimeframe = (dateStr: string) => {
    if (timeframe === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    if (timeframe === 'month') return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    if (timeframe === '30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      return date >= thirtyDaysAgo;
    }
    if (timeframe === 'year') return date.getFullYear() === now.getFullYear();
    return true;
  };

  const getClientStats = (clientId: string) => {
    const clientEntries = entries.filter(e => e.clientId === clientId && isInTimeframe(e.date));
    const clientPeriods = periods.filter(p => p.clientId === clientId && isInTimeframe(p.startDate));
    const hours = clientEntries.reduce((sum, e) => sum + e.hoursCalculated, 0) + clientPeriods.reduce((sum, p) => sum + p.totalHours, 0);
    const amount = clientEntries.reduce((sum, e) => sum + e.amountCalculated, 0) + clientPeriods.reduce((sum, p) => sum + p.totalAmount, 0);
    return { hours, amount };
  };

  const filteredClients = useMemo(() => clients.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
  ), [clients, search]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Clients</h1>
          <p className="text-sm text-slate-500 font-medium">Manage your relationships and billing profiles.</p>
        </div>
        <button onClick={handleOpenAdd} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition shadow-lg shadow-blue-500/10 active:scale-95">
          <Plus size={18} /> Add Client
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input type="text" placeholder="Search clients..." className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-blue-500 transition" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value as Timeframe)} className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none cursor-pointer">
            <option value="all">All Time History</option>
            <option value="month">This Month Only</option>
            <option value="30days">Last 30 Days</option>
            <option value="year">Full Year 2024</option>
          </select>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left">
            <thead className="bg-slate-50/50 text-slate-400 text-[9px] uppercase font-black tracking-widest">
              <tr>
                <th className="px-6 py-4">Client Name</th>
                <th className="px-6 py-4">Rate</th>
                <th className="px-6 py-4">Hours</th>
                <th className="px-6 py-4">Earned</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredClients.map((client) => {
                const stats = getClientStats(client.id);
                return (
                  <tr key={client.id} className="hover:bg-slate-50/50 transition cursor-pointer group" onClick={() => onViewClient(client.id)}>
                    <td className="px-6 py-5">
                      <div className="font-bold text-slate-900">{client.name}</div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{client.email || 'No email set'}</div>
                    </td>
                    <td className="px-6 py-5 font-bold text-slate-600 text-sm">{formatCurrency(client.defaultRate)}/hr</td>
                    <td className="px-6 py-5 font-bold text-slate-900 text-sm">{stats.hours.toFixed(2)}</td>
                    <td className="px-6 py-5 font-black text-blue-600 text-sm">{formatCurrency(stats.amount)}</td>
                    <td className="px-6 py-5 text-right">
                      <div className="flex items-center justify-end gap-1 md:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={(e) => handleOpenEdit(e, client)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Client"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          onClick={(e) => handleDelete(e, client.id, client.name)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Client"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-300 font-bold italic">No matching clients found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl p-8 space-y-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-blue-600"></div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {editingClient ? 'Revise Client Details' : 'Register New Client'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-300 hover:text-slate-600 transition">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Name (Business or Personal)</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-sm font-bold focus:border-blue-500 transition" placeholder="e.g. Acme Innovations" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Primary Billing Contact Email</label>
                <input type="email" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-sm font-bold focus:border-blue-500 transition" placeholder="accounts@acme.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Standard Hourly Rate ($)</label>
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 focus-within:border-blue-500 transition">
                  <span className="text-slate-400 font-bold">$</span>
                  <input required type="number" step="0.01" className="w-full py-3 bg-transparent outline-none text-sm font-bold" placeholder="0.00" value={formData.rate} onChange={e => setFormData({...formData, rate: parseFloat(e.target.value) || 0})} />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-600 text-xs uppercase tracking-widest hover:bg-slate-50 transition active:scale-95">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-700 transition shadow-lg shadow-blue-500/10 active:scale-95">
                  {editingClient ? 'Apply Changes' : 'Confirm Registration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
